import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, useApp } from '../bizsuite-app';
import { formatCurrencyShort } from '../utils/currency';

function Skeleton({ width, height, style }) {
  return (
    <div style={{
      width: width || '100%', height: height || 20,
      background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 6, ...style,
    }} />
  );
}

function HBarChart({ data, labelKey, valueKey, formatValue, colorFn }) {
  if (!data || data.length === 0) return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No data</div>;
  const max = Math.max(...data.map(d => parseFloat(d[valueKey]) || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((item, idx) => {
        const val = parseFloat(item[valueKey]) || 0;
        const w = (val / max) * 100;
        const color = colorFn ? colorFn(item, idx) : '#6366f1';
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 120, fontSize: 12, color: '#64748B', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item[labelKey]}>
              {item[labelKey] || 'Unknown'}
            </div>
            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 22, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(w, 2)}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ width: 80, fontSize: 12, fontWeight: 600, color: '#334155', textAlign: 'right', flexShrink: 0 }}>
              {formatValue ? formatValue(val) : val.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, sub, color, loading }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 12, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {loading ? <Skeleton width={80} height={32} /> : <div style={{ fontSize: 28, fontWeight: 700, color: color || '#1e293b' }}>{value}</div>}
      {sub && <div style={{ fontSize: 12, color: '#94A3B8' }}>{sub}</div>}
    </div>
  );
}

/* Revenue Waterfall */
function RevenueWaterfall({ data, loading }) {
  if (loading) return <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>{[1,2,3,4].map(i => <Skeleton key={i} width={80} height={120} />)}</div>;

  const items = [
    { label: 'Billed', value: data?.billed || 0, color: '#6366f1' },
    { label: 'Recognized', value: data?.recognized || 0, color: '#10b981' },
    { label: 'Deferred', value: data?.deferred || 0, color: '#f59e0b' },
    { label: 'Unbilled', value: data?.unbilled || 0, color: '#ef4444' },
  ];

  const maxVal = Math.max(...items.map(i => i.value), 1);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 24, height: 160, padding: '0 20px' }}>
      {items.map((item, idx) => {
        const h = (item.value / maxVal) * 120;
        return (
          <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{formatCurrencyShort(item.value)}</div>
            <div style={{
              width: 60, height: Math.max(h, 4), background: item.color,
              borderRadius: '4px 4px 0 0', transition: 'height 0.6s ease',
            }} />
            <div style={{ fontSize: 11, color: '#64748B' }}>{item.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/* Aging stacked bar */
function AgingBar({ data }) {
  const bucketColors = { '0-30': '#10b981', '31-60': '#f59e0b', '61-90': '#f97316', '90+': '#ef4444' };
  const bucketOrder = ['0-30', '31-60', '61-90', '90+'];

  const mapped = {};
  for (const row of (data || [])) {
    const bucket = row.bucket || row.aging_bucket || 'unknown';
    const normalBucket = bucket.includes('0') && bucket.includes('30') && !bucket.includes('60') ? '0-30'
      : bucket.includes('31') || (bucket.includes('30') && bucket.includes('60')) ? '31-60'
      : bucket.includes('61') || (bucket.includes('60') && bucket.includes('90')) ? '61-90'
      : bucket.includes('90') || bucket.includes('+') ? '90+' : bucket;
    mapped[normalBucket] = (mapped[normalBucket] || 0) + (parseFloat(row.total_amount) || 0);
  }

  const total = Object.values(mapped).reduce((s, v) => s + v, 0);
  if (total === 0) return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No aging data</div>;

  return (
    <div>
      <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', height: 32 }}>
        {bucketOrder.map(bucket => {
          const val = mapped[bucket] || 0;
          const w = (val / total) * 100;
          if (w < 1) return null;
          return (
            <div key={bucket} style={{
              width: `${w}%`, background: bucketColors[bucket] || '#94A3B8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 11, fontWeight: 600, transition: 'width 0.6s ease',
            }} title={`${bucket}: ${formatCurrencyShort(val)}`}>
              {w > 12 ? formatCurrencyShort(val) : ''}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8 }}>
        {bucketOrder.map(bucket => (
          <div key={bucket} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748B' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: bucketColors[bucket] }} />
            {bucket} days: {formatCurrencyShort(mapped[bucket] || 0)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FinanceDashboard() {
  const app = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboards/finance');
      if (res.ok) setData(await res.json());
    } catch (e) { /* best effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const d = data || {};

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>

      <p style={{ color: '#64748B', marginBottom: 20, marginTop: -16 }}>
        Financial overview, receivables, and cash flow analysis.
      </p>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <StatCard label="Total Billed" value={formatCurrencyShort(d.revenue_summary?.billed)} color="#6366f1" loading={loading} />
        <StatCard label="Recognized" value={formatCurrencyShort(d.revenue_summary?.recognized)} color="#10b981" loading={loading} />
        <StatCard label="DSO" value={`${d.dso || 0} days`} color={d.dso > 60 ? '#ef4444' : d.dso > 30 ? '#f59e0b' : '#10b981'} loading={loading} />
        <StatCard label="Cash Receipts" value={formatCurrencyShort(d.bank_balances?.total_receipts)} color="#10b981" loading={loading} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Revenue Waterfall */}
        <div className="card">
          <div className="card-header"><h2>Revenue Waterfall</h2></div>
          <div className="card-body">
            <RevenueWaterfall data={d.revenue_summary} loading={loading} />
          </div>
        </div>

        {/* Receivables Aging */}
        <div className="card">
          <div className="card-header"><h2>Receivables Aging</h2></div>
          <div className="card-body">
            {loading ? <Skeleton height={32} /> : <AgingBar data={d.receivables_aging || []} />}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Cash Flow */}
        <div className="card">
          <div className="card-header"><h2>Cash Inflows (Recent Months)</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (
              <HBarChart
                data={(d.cash_flow?.inflows || []).reverse().slice(0, 6)}
                labelKey="month"
                valueKey="inflow"
                formatValue={formatCurrencyShort}
                colorFn={() => '#10b981'}
              />
            )}
            {!loading && d.cash_flow?.monthly_salary_outflow > 0 && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 12, color: '#dc2626' }}>
                Est. Monthly Salary Outflow: {formatCurrencyShort(d.cash_flow.monthly_salary_outflow)}
              </div>
            )}
          </div>
        </div>

        {/* Salary Expense */}
        <div className="card">
          <div className="card-header"><h2>Salary Expense</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#64748B' }}>Monthly Cost</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>{formatCurrencyShort(d.salary_expense?.monthly)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#64748B' }}>Annual CTC</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>{formatCurrencyShort(d.salary_expense?.annual)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#64748B' }}>% of Revenue</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: d.salary_expense?.pct_of_revenue > 70 ? '#ef4444' : '#10b981' }}>
                      {d.salary_expense?.pct_of_revenue || 0}%
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profitability by Customer */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h2>Revenue by Customer</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3,4,5].map(i => <Skeleton key={i} height={22} />)}</div>
          ) : (
            <HBarChart
              data={(d.profitability_by_customer || []).slice(0, 10)}
              labelKey="customer_name"
              valueKey="revenue"
              formatValue={formatCurrencyShort}
              colorFn={(_, idx) => ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1'][idx % 10]}
            />
          )}
        </div>
      </div>

      {/* Invoice Status */}
      <div className="card">
        <div className="card-header"><h2>Invoice Status Breakdown</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
          ) : (d.invoice_status || []).length === 0 ? (
            <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No invoice data</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
              {(d.invoice_status || []).map((is, idx) => {
                const statusColors = { draft: '#94A3B8', sent: '#6366f1', paid: '#10b981', overdue: '#ef4444', cancelled: '#dc2626' };
                const color = statusColors[is.status] || '#94A3B8';
                return (
                  <div key={idx} style={{
                    padding: '16px 24px', borderRadius: 12, border: `2px solid ${color}20`,
                    background: `${color}08`, textAlign: 'center', minWidth: 120,
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color }}>{is.count}</div>
                    <div style={{ fontSize: 12, color: '#64748B', textTransform: 'capitalize' }}>{is.status}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{formatCurrencyShort(is.amount)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
